// Pure helpers for the messaging surfaces (issue #64). Extracted from
// the screens so the truncation and badge rules are unit-testable with
// zero mocks, following vote-math.ts's precedent.

import type { Message, MessagesPage } from '@/lib/api';

const PREVIEW_MAX = 80;

// One-line preview of a message body for the Matches list. Newlines are
// collapsed (a preview is a single line) and long bodies are truncated
// with an ellipsis. A message with no text can only be a photo (a link card
// needs its URL in the text), so that is what the preview says.
export function messagePreview(body: string, max: number = PREVIEW_MAX): string {
  const oneLine = body.replace(/\s+/g, ' ').trim();

  if (oneLine === '') return 'Photo';

  if (oneLine.length <= max) return oneLine;

  return `${oneLine.slice(0, max - 3).trimEnd()}...`;
}

// Badge label for a conversation's unread count: undefined when there is
// nothing to show, "9+" past nine so the badge never widens the row.
export function deriveUnreadLabel(count: number): string | undefined {
  if (count <= 0) return undefined;
  if (count > 9) return '9+';

  return String(count);
}

// What the chat screen should render, derived from the messages query's
// own state. Four outcomes, and the distinction between the middle two
// is a product decision, not a technicality:
//
// 'gone' is terminal. The backend answers the same 404 for a connection
// that does not exist, one the caller is not a party to, one that was
// revoked, and one where either side blocked the other
// (requireConnectionParticipant, issue #258's no-existence-oracle rule),
// so a retry can only ever 404 again -- the screen says the conversation
// is over and offers no way to loop. 'failed' is any other first-load
// failure, which a retry can genuinely fix. Collapsing the two is the
// easy mistake; this is the one place that decides it.
//
// A 404 outranks cached data: a conversation revoked mid-session is over,
// not stale, so a poll that starts 404ing swaps the thread out rather
// than leaving a dead conversation looking alive. Any OTHER error with
// data still cached stays 'ready' -- the poll failed, the messages on
// screen are still real, and blanking them would be the "feedback
// outruns the truth" bug in reverse.
export type ConversationStatus = 'loading' | 'gone' | 'failed' | 'ready';

export function conversationStatus(input: {
  isPending: boolean;
  isError: boolean;
  hasData: boolean;
  errorStatus: number | undefined;
}): ConversationStatus {
  if (input.isPending) return 'loading';
  if (input.isError && input.errorStatus === 404) return 'gone';
  if (input.isError && !input.hasData) return 'failed';

  return 'ready';
}

// The API returns pages newest-first, which is the order an inverted
// FlatList renders -- index 0 sits at the bottom of the screen. So
// flattening is a concatenation, not a reverse, and the screen no longer
// needs to reverse anything.
//
// Deduplicated by id: a 5s poll and a history fetch can both return the
// message at a page boundary, and rendering it twice would show the user
// a message they sent appearing to send itself again.
export function flattenMessagePages(pages: MessagesPage[]): Message[] {
  const seen = new Set<string>();
  const messages: Message[] = [];

  for (const page of pages) {
    for (const message of page.items) {
      if (seen.has(message.id)) continue;

      seen.add(message.id);
      messages.push(message);
    }
  }

  return messages;
}

// Whether an older page exists. A conversation holding exactly one page
// returns a cursor and then an empty page with none, so this reads the
// LAST page's cursor rather than the first's -- and the screen stops
// asking once it goes empty instead of re-requesting the same cursor.
export function hasOlderMessages(pages: MessagesPage[]): boolean {
  const last = pages[pages.length - 1];

  return last !== undefined && last.nextCursor !== '';
}

// The one line the collapsed composer shows. A draft that carries photos says
// so: the collapsed bar must never hide what is about to be sent.
export function composerPillLabel(draft: string, photoCount: number): string {
  if (photoCount > 0) return `Draft with ${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}. Tap to continue`;
  if (draft.trim() !== '') return 'Draft saved. Tap to continue';

  return 'Write a message';
}
